import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { AbstractControl, Validators } from '@angular/forms';
import { TypedFormBuilder, TypedFormGroup } from '@gmrappdev/formsafe';
import { Observable, Subject } from 'rxjs';
import { map, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { AircraftMakeModelHttpClient } from 'src/app/shared/flight-log-api/aircraft-make-models/aircraft-make-models-client.service';
import { AssignmentToDutyEntity } from 'src/store/assignment-to-duty/assignment-to-duty.model';
import { dateTime24HourFormat } from 'src/util/dateLib';
import { firstValueOrDefault } from 'src/util/rxjs/observables/first-value';
import { SelectItem, toSelectionList } from 'src/util/ui/forms';
import { AircraftMakeModelViewModel } from 'src/view-models/aircraft-make-model-view-model';
import { AssignmentToDutyViewModel } from 'src/view-models/assignment-to-duty-view-model';
import { PrdProcessingState } from 'src/view-models/pilot-view-model';
import { PrdDutyPositionType } from 'src/view-models/training-record-view-model';
import { PrdProcessingStateSelectItems } from '../pilot-form/pilot-form.component';

@Component({
  selector: 'fl-pilot-employment-record-assignment-to-duty-form',
  templateUrl: './pilot-employment-record-assignment-to-duty-form.component.html',
  styleUrls: ['./pilot-employment-record-assignment-to-duty-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PilotEmploymentRecordAssignmentToDutyFormComponent implements OnInit, OnDestroy {
  @Input()
  public assignmentToDuty?: AssignmentToDutyViewModel;
  @Input()
  public employmentRecordId: string;

  public form: TypedFormGroup<AssignmentToDutyEntity>;

  public aircraftMakeModelOptions$: Observable<SelectItem[]>;

  public aircraftMakeModelErrors$: Observable<string[]>;

  public aircraftMakeModelsearch$ = new Subject<string>();

  public selectedAircraftMakeModel: SelectItem;

  public _onDestroy$ = new Subject<void>();

  public processingStateOptions = PrdProcessingStateSelectItems;

  public dutyPositionOptions = Object.values(PrdDutyPositionType);

  public minDate = new Date(1950, 0, 1);
  public maxDate = new Date(Date.now());
  public minEndDate: Date;
  public dateTimeFormat = dateTime24HourFormat;

  constructor(
    private _tfb: TypedFormBuilder,
    private _aircraftMakeModel: AircraftMakeModelHttpClient,
    private _cf: ChangeDetectorRef,
  ) {}

  public ngOnInit() {
    const assignmentToDuty: Partial<AssignmentToDutyViewModel> =
      this.assignmentToDuty != null ? this.assignmentToDuty : {};

    this.form = this._tfb.group<AssignmentToDutyEntity>({
      id: [assignmentToDuty.id],
      employmentRecordId: [this.employmentRecordId],
      aircraftMakeModelId: [assignmentToDuty.aircraftMakeModelId, Validators.required],
      dutyPositionType: [assignmentToDuty.dutyPositionType, Validators.required],
      endDate: [assignmentToDuty.endDate],
      lastProcessedDate: [assignmentToDuty.lastProcessedDate],
      prdSourceSystemId: [assignmentToDuty.prdSourceSystemId],
      processingError: [assignmentToDuty.processingError],
      processingState: [
        assignmentToDuty.processingState ?? PrdProcessingState.ReadyForTransmission,
      ],
      startDate: [assignmentToDuty.startDate, Validators.required],
      suppressed: [assignmentToDuty.suppressed ?? false],
    });

    if (this.assignmentToDuty?.aircraftMakeModelId != null) {
      this.setSelectedAircraftMakeModel(this.assignmentToDuty?.aircraftMakeModelId);
    }

    this.aircraftMakeModelOptions$ = this.observeAircraftMakeModelOptions(
      this.aircraftMakeModelsearch$,
    );

    this.aircraftMakeModelErrors$ = this.form.controls.aircraftMakeModelId.statusChanges.pipe(
      map((status) => {
        if (this.form.controls.aircraftMakeModelId.valid) {
          return [];
        } else {
          const errors: string[] = [];
          if (this.form.controls.aircraftMakeModelId.hasError('required')) {
            errors.push('Reason is required');
          }
          return errors;
        }
      }),
    );

    this.form.controls.startDate.valueChanges
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((value) => {
        if (this.form.touched) {
          this.minEndDate = value;
        }
      });

    this.form.controls.endDate.valueChanges.pipe(takeUntil(this._onDestroy$)).subscribe(() => {
      if (this.form.touched) {
      }
    });
  }

  public onAircraftMakeModelSearch(searchTerm: string) {
    return this.aircraftMakeModelsearch$.next(searchTerm);
  }

  public ngOnDestroy() {
    this._onDestroy$.next();
  }

  public getControlRequiredError(control: AbstractControl) {
    if (control.valid) {
      return [];
    } else {
      const errors: string[] = [];
      if (control.hasError('required')) {
        errors.push('Reason is required');
      }
      return errors;
    }
  }

  public async setSelectedAircraftMakeModel(id: string) {
    this.selectedAircraftMakeModel = {
      value: id,
      label: await firstValueOrDefault(
        this._aircraftMakeModel.getAll({ id }).pipe(map((r) => r.results[0].code)),
        'Unknown',
      ),
    };
    this._cf.markForCheck();
  }

  public observeAircraftMakeModelOptions(searchTerm: Observable<string | null>) {
    return searchTerm
      .pipe(startWith(null as string | null))
      .pipe(
        switchMap((search) =>
          this._aircraftMakeModel.getAll({
            codeLike: search ?? undefined,
            take: 200,
          }),
        ),
      )
      .pipe(map((r) => r.results))
      .pipe(
        map(
          toSelectionList<AircraftMakeModelViewModel>(
            (aircraftMakeModel) => `${aircraftMakeModel.code}`,
            'id',
          ),
        ),
      );
  }
}
